import { Center, Text } from '@mantine/core';
import { distance } from '@turf/distance';
import { point } from '@turf/helpers';
import { useMemo } from 'react';
import { Marker } from 'react-map-gl';

import type { FlattenedIlotPointFeature } from '@fields/data-access';

function getStatusFillColor(status: string | null | undefined): string | null {
  const normalized = (status ?? '').trim().toUpperCase();
  switch (normalized) {
    case 'INCOMPLETE':
      return '#e03131'; // rouge
    case 'PENDING':
      return '#f59f00'; // orange
    case 'FINISHED':
      return '#40c057'; // green
    case 'IMPOSSIBLE':
      return '#adb5bd'; // grey
    default:
      return null;
  }
}

interface GroupedMarker {
  feature: FlattenedIlotPointFeature;
  displayPosition: [number, number];
}

interface MarkerGroup {
  centerPosition: [number, number];
  markers: GroupedMarker[];
  isGrouped: boolean;
}

export interface IlotPointsLayerProps {
  points: FlattenedIlotPointFeature[];
  size?: number;
  withLabel?: boolean;
  groupingThreshold?: number;
  maxGroupRadius?: number;
  activeSampleId?: number | null;
  activePointCode?: string | null;
  onPointClick?: (feature: FlattenedIlotPointFeature) => void;
}

export function IlotPointsLayer({
  points,
  size = 24,
  withLabel = true,
  groupingThreshold = 10,
  maxGroupRadius = 15,
  activeSampleId = null,
  activePointCode = null,
  onPointClick,
}: IlotPointsLayerProps) {
  const groupedMarkers = useMemo(() => {
    const groups: MarkerGroup[] = [];
    const processed = new Set<string>();

    points.forEach((feature) => {
      const coords = feature.geometry.coordinates as [number, number];
      const key = `${coords[0]}-${coords[1]}`;
      if (processed.has(key)) return;

      const nearby = points.filter((other) => {
        const otherCoords = other.geometry.coordinates as [number, number];
        const otherKey = `${otherCoords[0]}-${otherCoords[1]}`;
        if (processed.has(otherKey)) return false;
        const dist = distance(point(coords), point(otherCoords), {
          units: 'meters',
        });
        return dist <= groupingThreshold;
      });

      nearby.forEach((f) => {
        const c = f.geometry.coordinates as [number, number];
        processed.add(`${c[0]}-${c[1]}`);
      });

      const markers: GroupedMarker[] = [];
      if (nearby.length === 1) {
        markers.push({ feature: nearby[0], displayPosition: coords });
      } else {
        nearby.forEach((f, index) => {
          const angle = (2 * Math.PI * index) / nearby.length;
          const offsetX = (maxGroupRadius / 111000) * Math.cos(angle);
          const offsetY = (maxGroupRadius / 111000) * Math.sin(angle);
          markers.push({
            feature: f,
            displayPosition: [
              coords[0] + offsetX,
              coords[1] + offsetY,
            ],
          });
        });
      }

      groups.push({
        centerPosition: coords,
        markers,
        isGrouped: nearby.length > 1,
      });
    });

    return groups;
  }, [points, groupingThreshold, maxGroupRadius]);

  if (!points?.length) return null;

  return (
    <>
      {groupedMarkers.flatMap((group) =>
        group.markers.map(({ feature, displayPosition }, index) => {
          const { label, radius, color, status, fieldwork_allowed } =
            feature.properties;
          const fieldworkAllowed = fieldwork_allowed !== false;
          const fillColor = fieldworkAllowed
            ? getStatusFillColor(status) ?? color
            : color;
          const isInActiveSample =
            activeSampleId !== null &&
            feature.properties.sampleId === activeSampleId;
          const isActivePoint =
            isInActiveSample &&
            activePointCode !== null &&
            feature.properties.code === activePointCode;
          const markerSize = size + (isInActiveSample ? 8 : 0) + (isActivePoint ? 4 : 0);
          return (
            <Marker
              anchor="center"
              key={feature.id ?? `${displayPosition[0]}-${displayPosition[1]}-${index}`}
              latitude={displayPosition[1]}
              longitude={displayPosition[0]}
            >
              <Center
                onClick={(e) => {
                  if (!fieldworkAllowed) {
                    return;
                  }
                  // Prevent the map click handler from re-selecting the ilot and
                  // resetting sample selection.
                  e.stopPropagation();
                  e.preventDefault();
                  onPointClick?.(feature);
                }}
                onTouchEnd={(e) => {
                  if (!fieldworkAllowed) {
                    return;
                  }
                  // iOS can turn taps into both touch + click; stop both.
                  e.stopPropagation();
                  onPointClick?.(feature);
                }}
                style={{
                  backgroundColor: fillColor,
                  borderRadius: radius,
                  width: markerSize,
                  height: markerSize,
                  border: '2px solid white',
                  cursor:
                    onPointClick && fieldworkAllowed ? 'pointer' : 'default',
                  opacity: fieldworkAllowed ? (group.isGrouped ? 0.9 : 1) : 0.55,
                }}
              >
                {withLabel ? (
                  <Text c="white" fw="bold" lh={1} size="xs">
                    {label}
                  </Text>
                ) : null}
              </Center>
            </Marker>
          );
        }),
      )}
    </>
  );
}

export default IlotPointsLayer;
